import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { useRef } from 'react';
import { AnnotationTypes } from '@atlaskit/adf-schema';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, CONTENT_COMPONENT, EVENT_TYPE, RESOLVE_METHOD } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { getAnnotationInlineNodeTypes, getRangeInlineNodeNames } from '@atlaskit/editor-common/utils';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { closeComponent, createAnnotation, removeInlineCommentNearSelection, setInlineCommentDraftState, updateInlineCommentResolvedState } from '../editor-commands';
import { getAllAnnotations, getAnnotationViewKey, getSelectionPositions } from '../pm-plugins/utils';
import { AnnotationTestIds } from '../types';
import { AnnotationViewWrapper } from './AnnotationViewWrapper';
var findPosForDOM = function findPosForDOM(sel) {
  var $from = sel.$from,
    from = sel.from;

  // Retrieve current TextNode
  var index = $from.index();
  var node = index < $from.parent.childCount && $from.parent.child(index);

  // Right edge of a mark.
  if (!node && $from.nodeBefore && $from.nodeBefore.isText && $from.nodeBefore.marks.find(function (mark) {
    return mark.type.name === 'annotation';
  })) {
    return from - 1;
  }
  return from;
};
var selector = function selector(states) {
  var _states$annotationSta, _states$annotationSta2, _states$annotationSta3, _states$annotationSta4, _states$annotationSta5, _states$annotationSta6, _states$annotationSta7;
  return {
    annotations: (_states$annotationSta = states.annotationState) === null || _states$annotationSta === void 0 ? void 0 : _states$annotationSta.annotations,
    bookmark: (_states$annotationSta2 = states.annotationState) === null || _states$annotationSta2 === void 0 ? void 0 : _states$annotationSta2.bookmark,
    isInlineCommentViewClosed: (_states$annotationSta3 = states.annotationState) === null || _states$annotationSta3 === void 0 ? void 0 : _states$annotationSta3.isInlineCommentViewClosed,
    isOpeningMediaCommentFromToolbar: (_states$annotationSta4 = states.annotationState) === null || _states$annotationSta4 === void 0 ? void 0 : _states$annotationSta4.isOpeningMediaCommentFromToolbar,
    selectAnnotationMethod: (_states$annotationSta5 = states.annotationState) === null || _states$annotationSta5 === void 0 ? void 0 : _states$annotationSta5.selectAnnotationMethod,
    selectedAnnotations: (_states$annotationSta6 = states.annotationState) === null || _states$annotationSta6 === void 0 ? void 0 : _states$annotationSta6.selectedAnnotations,
    isAnnotationManagerEnabled: (_states$annotationSta7 = states.annotationState) === null || _states$annotationSta7 === void 0 ? void 0 : _states$annotationSta7.isAnnotationManagerEnabled
  };
};
export function InlineCommentView(_ref) {
  var providers = _ref.providers,
    editorView = _ref.editorView,
    editorAnalyticsAPI = _ref.editorAnalyticsAPI,
    editorAPI = _ref.editorAPI,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent;
  // As inlineComment is the only annotation present, this function is not generic
  var inlineCommentProvider = providers.inlineComment;
  var state = editorView.state,
    dispatch = editorView.dispatch;
  var lastSelectedAnnotationId = useRef();
  var CreateComponent = inlineCommentProvider.createComponent,
    ViewComponent = inlineCommentProvider.viewComponent;
  var _useSharedPluginState = useSharedPluginStateWithSelector(editorAPI, ['annotation'], selector),
    annotations = _useSharedPluginState.annotations,
    bookmark = _useSharedPluginState.bookmark,
    isInlineCommentViewClosed = _useSharedPluginState.isInlineCommentViewClosed,
    isOpeningMediaCommentFromToolbar = _useSharedPluginState.isOpeningMediaCommentFromToolbar,
    selectAnnotationMethod = _useSharedPluginState.selectAnnotationMethod,
    selectedAnnotations = _useSharedPluginState.selectedAnnotations,
    isAnnotationManagerEnabled = _useSharedPluginState.isAnnotationManagerEnabled;
  var annotationsList = getAllAnnotations(editorView.state.doc);
  var selection = getSelectionPositions(state, bookmark);
  var position = findPosForDOM(selection);
  var dom;
  try {
    // Ignored via go/ees005
    // eslint-disable-next-line @atlaskit/editor/no-as-casting
    dom = findDomRefAtPos(position, editorView.domAtPos.bind(editorView));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error);
    if (dispatchAnalyticsEvent) {
      var payload = {
        action: ACTION.ERRORED,
        actionSubject: ACTION_SUBJECT.CONTENT_COMPONENT,
        eventType: EVENT_TYPE.OPERATIONAL,
        attributes: {
          component: CONTENT_COMPONENT.INLINE_COMMENT,
          selection: selection.toJSON(),
          position: position,
          docSize: editorView.state.doc.nodeSize,
          error: error.toString()
        }
      };
      dispatchAnalyticsEvent(payload);
    }
  }

  // Network Status
  var networkStatusSelector = useSharedPluginStateSelector(editorAPI, 'connectivity.mode', {
    disabled: editorExperiment('platform_editor_offline_editing_web', false)
  });
  if (!dom) {
    return null;
  }

  // Create Component
  if (bookmark) {
    var _selectedAnnotations$;
    if (!CreateComponent) {
      return null;
    }
    var currentlySelectedAnnotation = selectedAnnotations === null || selectedAnnotations === void 0 || (_selectedAnnotations$ = selectedAnnotations[0]) === null || _selectedAnnotations$ === void 0 ? void 0 : _selectedAnnotations$.id;
    var isAnnotationSelectionChanged = currentlySelectedAnnotation !== lastSelectedAnnotationId.current;

    // Update the last selected annotation ID if the selection was updated
    if (isAnnotationSelectionChanged) {
      lastSelectedAnnotationId.current = currentlySelectedAnnotation;
    }
    var inlineNodeTypes = getRangeInlineNodeNames({
      doc: state.doc,
      pos: selection
    });

    //getting all text between bookmarked positions
    var textSelection = state.doc.textBetween(selection.from, selection.to);
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": AnnotationTestIds.floatingComponent,
      "data-editor-popup": "true"
    }, /*#__PURE__*/React.createElement(CreateComponent, {
      dom: dom,
      textSelection: textSelection,
      wasNewAnnotationSelected: !!currentlySelectedAnnotation && isAnnotationSelectionChanged,
      onCreate: function onCreate(id) {
        if (!isAnnotationManagerEnabled) {
          var createAnnotationResult = createAnnotation(editorAnalyticsAPI, editorAPI)(id, AnnotationTypes.INLINE_COMMENT, inlineCommentProvider.supportedBlockNodes)(editorView.state, editorView.dispatch);
          !editorView.hasFocus() && editorView.focus();
          if (!createAnnotationResult) {
            throw new Error('Failed to create annotation');
          }
        }
      },
      onClose: function onClose() {
        if (!isAnnotationManagerEnabled) {
          setInlineCommentDraftState(editorAnalyticsAPI, undefined, editorAPI)(false)(editorView.state, editorView.dispatch);
          !editorView.hasFocus() && editorView.focus();
        }
      },
      inlineNodeTypes: inlineNodeTypes,
      isOpeningMediaCommentFromToolbar: isOpeningMediaCommentFromToolbar,
      isOffline: networkStatusSelector === 'offline'
    }));
  }

  // View Component
  var activeAnnotations = (selectedAnnotations === null || selectedAnnotations === void 0 ? void 0 : selectedAnnotations.filter(function (mark) {
    return annotations && annotations[mark.id] === false;
  })) || [];
  if (!ViewComponent || activeAnnotations.length === 0) {
    return null;
  }
  var onAnnotationViewed = function onAnnotationViewed() {
    var _editorView$state$doc;
    if (!dispatchAnalyticsEvent) {
      return;
    }

    // fire analytics
    var payload = {
      action: ACTION.VIEWED,
      actionSubject: ACTION_SUBJECT.ANNOTATION,
      actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
      eventType: EVENT_TYPE.TRACK,
      attributes: {
        overlap: activeAnnotations.length ? activeAnnotations.length - 1 : 0,
        targetNodeType: (_editorView$state$doc = editorView.state.doc.nodeAt(position)) === null || _editorView$state$doc === void 0 ? void 0 : _editorView$state$doc.type.name,
        method: selectAnnotationMethod
      }
    };
    dispatchAnalyticsEvent(payload);
  };
  if (isInlineCommentViewClosed || !selectedAnnotations) {
    return null;
  }

  // For view mode, the finding of inline node types is a bit more complex,
  // that's why we will not provide it as a `inlineNodeTypes` props to the view component,
  // to speed up the rendering process.
  var getInlineNodeTypes = function getInlineNodeTypes(annotationId) {
    return getAnnotationInlineNodeTypes(editorView.state, annotationId);
  };
  return /*#__PURE__*/React.createElement(AnnotationViewWrapper, {
    "data-editor-popup": "true",
    "data-testid": AnnotationTestIds.floatingComponent,
    key: getAnnotationViewKey(activeAnnotations),
    onViewed: onAnnotationViewed
  }, /*#__PURE__*/React.createElement(ViewComponent, {
    annotationsList: annotationsList,
    annotations: activeAnnotations,
    getInlineNodeTypes: getInlineNodeTypes,
    dom: dom,
    onDelete: function onDelete(id) {
      return removeInlineCommentNearSelection(id, inlineCommentProvider.supportedBlockNodes)(editorView.state, dispatch);
    },
    onResolve: function onResolve(id) {
      return updateInlineCommentResolvedState(editorAnalyticsAPI)(_defineProperty({}, id, true), RESOLVE_METHOD.COMPONENT)(editorView.state, editorView.dispatch);
    },
    onClose: function onClose() {
      closeComponent()(editorView.state, editorView.dispatch);
    },
    isOpeningMediaCommentFromToolbar: isOpeningMediaCommentFromToolbar,
    isOffline: networkStatusSelector === 'offline'
  }));
}